import { useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/common/Button';
import { Icon } from '../components/common/Icon';
import { Reveal } from '../components/animation/Reveal';
import { LoadingScreen } from '../components/common/Spinner';
import { ProductCard } from '../components/product/ProductCard';
import { productRepository } from '../services/productRepository';
import { breadcrumbSchema } from '../lib/seo';
import { useCrop } from '../hooks';

export default function CropDetailPage() {
  const { slug = '' } = useParams();
  const { data: crop, isLoading } = useCrop(slug);

  if (isLoading) {
    return (
      <div className="pt-[var(--nav-h)]">
        <LoadingScreen label="Loading crop" />
      </div>
    );
  }

  if (!crop) {
    return (
      <>
        <PageHeader title="Crop not found" subtitle="We don’t have a solution page for that crop yet." />
        <div className="container-x py-16">
          <Button to="/crop-solutions" variant="primary" size="lg">
            <Icon name="ArrowLeft" className="h-5 w-5" /> All crop solutions
          </Button>
        </div>
      </>
    );
  }

  const products = crop.productSlugs
    .map((s) => productRepository.getProductBySlugSync(s))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const accent = products[0]?.accentColor ?? '#159A3C';
  const enquireTo = products[0] ? `/inquiry/${products[0].id}` : '/catalog';
  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Crop Solutions', path: '/crop-solutions' },
    { name: crop.name, path: `/crop-solutions/${crop.slug}` },
  ];

  return (
    <>
      <Seo
        title={`${crop.name} — Recommended Products & Programme | GreenbayBio`}
        description={`See GreenbayBio products recommended for ${crop.name} from verified crop programmes.`}
        path={`/crop-solutions/${crop.slug}`}
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <PageHeader eyebrow={`${crop.group ?? 'Crop'} · Crop solution`} title={crop.name} subtitle={crop.intro} accent={accent} />

      <div className="container-x py-14">
        {crop.note && (
          <Reveal className="mb-10 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50/60 p-6" y={18}>
            <Icon name="Info" className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
            <div>
              <p className="font-semibold text-brand-900">Programme note</p>
              <p className="mt-1 text-brand-800">{crop.note}</p>
            </div>
          </Reveal>
        )}

        <h2 className="mb-6 font-display text-h2 font-semibold text-ink">Recommended products for {crop.name}</h2>
        {products.length > 0 ? (
          <Reveal className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.1} y={26}>
            {products.map((p) => (
              <ProductCard key={p.slug} product={p} />
            ))}
          </Reveal>
        ) : (
          <p className="rounded-xl border border-dashed border-sand-200 bg-white px-6 py-12 text-center text-ink-500">
            No products are currently tagged for this crop.
          </p>
        )}

        <div className="mt-12 rounded-xl border border-sand-200 bg-sand-100/50 p-8 text-center">
          <h3 className="font-display text-h3 font-semibold text-ink">Not sure where to start?</h3>
          <p className="mx-auto mt-2 max-w-md text-ink-500">
            Tell us about your {crop.name.toLowerCase()} crop and our team will help you build the right programme.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button to={enquireTo} variant="primary" size="lg">
              Enquire now <Icon name="ArrowRight" className="h-5 w-5" />
            </Button>
            <Button to="/crop-solutions" variant="outline" size="lg">
              Other crops
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
